/* <ldy-collection> (sections/ldy-main-collection.liquid): instant filters and sort through the Section Rendering API,
   active-filter chips, "Load more", and the filter sheet on phones (the one filter form moves into the sheet and back).
   The URL always carries the filters, so a filtered page can be shared or reloaded. */
(() => {
  if (customElements.get('ldy-collection')) return;
  const fill = (str, map) => str.replace(/\[(\w+)\]/g, (_, k) => map[k] ?? '');
  const DESKTOP = '(min-width: 990px)';

  class LdyCollection extends HTMLElement {
    connectedCallback() {
      if (!this.ready) this.setup();
      this.onPop = () => this.refresh(location.href, false);
      this.onResize = () => {
        this.measureHeader();
        if (this.sheet?.open && matchMedia(DESKTOP).matches) this.sheet.close(); // the form goes back to the sidebar
      };
      window.addEventListener('popstate', this.onPop);
      window.addEventListener('resize', this.onResize, { passive: true });
      this.measureHeader();
    }

    disconnectedCallback() {
      window.removeEventListener('popstate', this.onPop);
      window.removeEventListener('resize', this.onResize);
      this.controller?.abort();
      this.moreCtl?.abort();
    }

    /* Listeners on the element itself survive moves, so they are added once */
    setup() {
      this.ready = true;
      this.form = this.querySelector('[data-filters]');
      this.aside = this.querySelector('[data-aside]');
      this.sheet = this.querySelector('[data-filter-sheet]');
      this.status = this.querySelector('[data-status]');
      this.addEventListener('change', (e) => this.onChange(e));
      this.addEventListener('input', (e) => e.target.matches('[data-price]') && this.priceLabel(e.target));
      this.addEventListener('click', (e) => this.onClick(e));
      this.form?.addEventListener('submit', (e) => {
        e.preventDefault();
        this.refresh(this.urlFromForm());
      });
      this.sheet?.addEventListener('close', () => this.form && this.aside?.append(this.form));
    }

    /* The phone Filter/Sort bar sticks just under the sticky header */
    measureHeader() {
      const header = document.querySelector('.section-header');
      if (header) this.style.setProperty('--ldy-head', `${header.offsetHeight}px`);
    }

    onChange(e) {
      const t = e.target;
      if (t.matches('[data-sort]')) {
        this.querySelectorAll('[data-sort]').forEach((s) => (s.value = t.value));
        this.refresh(this.urlFromForm());
      } else if (this.form?.contains(t)) {
        this.refresh(this.urlFromForm());
      }
    }

    onClick(e) {
      const t = e.target;
      const nav = t.closest('a[data-nav]');
      if (nav) {
        e.preventDefault();
        this.refresh(nav.href);
        return;
      }
      const more = t.closest('a[data-more]');
      if (more) {
        e.preventDefault();
        this.loadMore(more);
        return;
      }
      const showAll = t.closest('[data-fmore]');
      if (showAll) {
        this.toggleGroup(showAll);
        return;
      }
      if (t.closest('[data-open-filters]')) {
        this.querySelector('[data-sheet-body]').append(this.form);
        this.sheet.showModal();
        return;
      }
      if (this.sheet && (t === this.sheet || t.closest('[data-close]'))) this.sheet.close();
    }

    /* "Show all (42)" on long filter lists; the open state survives re-renders (keyed by the list's id) */
    toggleGroup(button, open = button.getAttribute('aria-expanded') !== 'true') {
      const row = document.getElementById(button.getAttribute('aria-controls'));
      if (!row) return;
      this.openGroups = this.openGroups || new Set();
      if (open) this.openGroups.add(row.id);
      else this.openGroups.delete(row.id);
      if (!button.dataset.more) button.dataset.more = button.textContent.trim();
      row.classList.toggle('is-open', open);
      button.setAttribute('aria-expanded', String(open));
      button.textContent = open ? button.dataset.less : button.dataset.more;
    }

    priceLabel(range) {
      const money = window.ldyCommerce?.formatMoney(Number(range.value) * 100) || range.value;
      const text = fill(this.dataset.upTo, { amount: String(money).replace(/[.,]00(?=\D*$)/, '') });
      range.setAttribute('aria-valuetext', text);
      const out = range.parentElement.querySelector('[data-price-out]');
      if (out) out.textContent = text;
    }

    urlFromForm() {
      const params = new URLSearchParams();
      const range = this.form?.querySelector('[data-price]');
      if (this.form) {
        for (const [key, value] of new FormData(this.form)) {
          if (value === '') continue;
          // the slider at its top means "any price"
          if (range && key === range.name && Number(value) >= Number(range.max)) continue;
          params.append(key, value);
        }
      }
      const sort = this.querySelector('[data-sort]')?.value;
      if (sort && sort !== this.dataset.defaultSort) params.set('sort_by', sort);
      const query = params.toString();
      return `${location.pathname}${query ? `?${query}` : ''}`;
    }

    async fetchSection(url, signal) {
      const u = new URL(url, location.href);
      u.searchParams.set('section_id', this.dataset.section);
      const res = await fetch(u, { signal });
      if (!res.ok) throw new Error(`Section request failed (${res.status})`);
      return new DOMParser().parseFromString(await res.text(), 'text/html');
    }

    async refresh(url, push = true) {
      this.controller?.abort();
      this.moreCtl?.abort(); // a "Load more" still on its way belongs to the old filters
      const ctl = new AbortController();
      this.controller = ctl;
      const results = this.querySelector('[data-results]');
      results.setAttribute('aria-busy', 'true');
      try {
        const doc = await this.fetchSection(url, ctl.signal);
        this.paint(doc);
        if (push) history.pushState({ ldyCollection: true }, '', url);
      } catch (err) {
        if (err.name === 'AbortError') return;
        console.error('[ldy-collection]', err);
        if (!push) {
          location.reload(); // back/forward already changed the URL: load that page for real
          return;
        }
        window.ldyCommerce?.toast({ title: this.dataset.error, tone: 'error' });
      } finally {
        if (this.controller === ctl) results.removeAttribute('aria-busy');
      }
    }

    paint(doc) {
      const results = this.querySelector('[data-results]');
      const active = document.activeElement;
      const inForm = active && this.form?.contains(active);
      const inResults = active && results.contains(active);
      const focusKey = inForm ? `${active.name}|${active.type === 'checkbox' ? active.value : ''}` : '';
      for (const sel of ['[data-results]', '[data-filters]', '[data-filter-count]', '[data-show-count]']) {
        const from = doc.querySelector(sel);
        const to = this.querySelector(sel);
        if (from && to) to.innerHTML = from.innerHTML;
      }
      const clearFrom = doc.querySelector('.ldy-coll__sheetbar [data-nav]');
      const clearTo = this.querySelector('.ldy-coll__sheetbar [data-nav]');
      if (clearFrom && clearTo) clearTo.href = clearFrom.getAttribute('href');
      const sort = doc.querySelector('[data-sort]')?.value;
      if (sort) this.querySelectorAll('[data-sort]').forEach((s) => (s.value = sort));
      for (const id of this.openGroups || []) {
        const button = this.querySelector(`[data-fmore][aria-controls="${id}"]`);
        if (button) this.toggleGroup(button, true);
      }

      const count = this.querySelector('[data-count-text]');
      if (this.status && count) this.status.textContent = count.textContent.trim();
      let restored = false;
      if (focusKey) {
        const again = [...this.form.elements].find(
          (el) => `${el.name}|${el.type === 'checkbox' ? el.value : ''}` === focusKey,
        );
        again?.focus({ preventScroll: true });
        restored = Boolean(again);
      }
      // the control that had focus is gone (a removed chip, "Clear all", a value with no pieces left)
      if ((inForm && !restored) || inResults) count?.focus({ preventScroll: true });
    }

    async loadMore(link) {
      if (link.getAttribute('aria-busy')) return;
      link.setAttribute('aria-busy', 'true');
      this.moreCtl?.abort();
      const ctl = new AbortController();
      this.moreCtl = ctl;
      try {
        const doc = await this.fetchSection(link.href, ctl.signal);
        const incoming = [...(doc.querySelector('[data-grid]')?.children || [])];
        this.querySelector('[data-grid]').append(...incoming);
        const box = this.querySelector('[data-more-box]');
        const next = doc.querySelector('[data-more-box]');
        if (next) box?.replaceWith(next);
        else box?.remove();
        incoming[0]?.querySelector('a[href]')?.focus({ preventScroll: true }); // keyboard users continue at the new pieces
      } catch (err) {
        link.removeAttribute('aria-busy');
        if (err.name === 'AbortError') return;
        console.error('[ldy-collection] load more', err);
        window.ldyCommerce?.toast({ title: this.dataset.error, tone: 'error' });
      }
    }
  }

  customElements.define('ldy-collection', LdyCollection);
})();
